import { dataFreshnessSchema } from "@adpilot/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  type DashboardPerformanceScope,
  dashboardPerformanceQueries,
} from "@/hooks/use-dashboard-performance";
import { api } from "@/lib/api";
import { subscribeToDataStatus } from "@/lib/data-status-events";
import { SSE_FALLBACK_POLL_MS, SSE_SAFETY_POLL_MS } from "@/lib/sse-invalidation";

const responseSchema = z.object({
  success: z.literal(true),
  data: z.object({ ads: dataFreshnessSchema, sales: dataFreshnessSchema }),
});

export function useDashboardDataFreshness(scope: DashboardPerformanceScope) {
  const { workspaceId, shopIds, startDate, endDate, timeZone, granularity } = scope;
  const client = useQueryClient();
  const [streamFailed, setStreamFailed] = useState(false);
  const shopKey = shopIds.join(",");
  const asinKey = [...(scope.asins ?? [])].sort().join(",");
  const query = useQuery({
    queryKey: ["dashboard", "data-freshness", workspaceId, shopKey, startDate, endDate],
    queryFn: async ({ signal }) => {
      const response = await api
        .get("dashboard/data-freshness", {
          signal,
          searchParams: { shopIds: shopKey, startDate, endDate },
        })
        .json();
      return responseSchema.parse(response).data;
    },
    enabled: Boolean(workspaceId) && shopIds.length > 0,
    refetchInterval: streamFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });
  useEffect(() => {
    if (!workspaceId) return;
    const freshness = subscribeToDataStatus(
      client,
      workspaceId,
      ["dashboard", "data-freshness", workspaceId, shopKey, startDate, endDate],
      (state) => setStreamFailed(state === "error"),
    );
    if (!shopKey) return freshness;
    const performance = dashboardPerformanceQueries({
      workspaceId,
      shopIds: shopKey.split(","),
      startDate,
      endDate,
      timeZone,
      granularity,
      asins: asinKey ? asinKey.split(",") : [],
    });
    const sales = subscribeToDataStatus(client, workspaceId, performance.sales.queryKey);
    const ads = subscribeToDataStatus(client, workspaceId, performance.ads.queryKey);
    return () => {
      freshness();
      sales();
      ads();
    };
  }, [client, workspaceId, shopKey, startDate, endDate, timeZone, granularity, asinKey]);
  return query;
}
